import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { browseTutors, getTutorProfile } from '../api/endpoints';
import {
  Search, Star, Clock, Globe, GraduationCap, Filter,
  ChevronRight, Users, Shield, Award, X
} from 'lucide-react';

const TutoringPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isRegisteredTutor, setIsRegisteredTutor] = useState(false);

  // Filters
  const [searchSubject, setSearchSubject] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [maxRate, setMaxRate] = useState('');
  const [minRating, setMinRating] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadTutors();
    checkTutorStatus();
  }, [page]);

  const checkTutorStatus = async () => {
    try {
      await getTutorProfile();
      setIsRegisteredTutor(true);
    } catch {
      setIsRegisteredTutor(false);
    }
  };

  const loadTutors = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 12 };
      if (searchSubject) params.subject = searchSubject;
      if (selectedLanguage) params.language = selectedLanguage;
      if (maxRate) params.max_rate = parseFloat(maxRate);
      if (minRating) params.min_rating = parseFloat(minRating);

      const res = await browseTutors(params);
      setTutors(res.data.tutors || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      setError('Failed to load tutors');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadTutors();
  };

  const clearFilters = () => {
    setSearchSubject('');
    setSelectedLanguage('');
    setMaxRate('');
    setMinRating('');
    setPage(1);
    setTimeout(loadTutors, 0);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
      />
    ));
  };

  return (
    <Layout>
      <div className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a192f 0%, #0d2847 100%)' }}>
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Find Your <span className="text-cyan-400">Perfect Tutor</span>
              </h1>
              <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
                Connect with verified tutors who are ahead in their studies.
                Get personalised help with your subjects.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="max-w-2xl mx-auto flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by subject (e.g., Cardiology, Chemistry, SAT Math)..."
                    value={searchSubject}
                    onChange={(e) => setSearchSubject(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-xl border-0 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-cyan-400"
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-3 rounded-xl text-white font-semibold transition-all hover:opacity-90 relative overflow-hidden"
                  style={{ background: 'var(--gradient-primary)', boxShadow: 'var(--shadow-primary)' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full" style={{ animation: 'shimmer 2.5s infinite' }} />
                  <span className="relative z-10">Search</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-4 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all"
                >
                  <Filter className="w-5 h-5" />
                </button>
              </form>

              {/* Filter Panel */}
              {showFilters && (
                <div className="max-w-2xl mx-auto mt-4 p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-slate-300 mb-1">Language</label>
                      <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-white text-slate-900 text-sm"
                      >
                        <option value="">All Languages</option>
                        <option value="English">English</option>
                        <option value="Mandarin">Mandarin</option>
                        <option value="Hindi">Hindi</option>
                        <option value="Spanish">Spanish</option>
                        <option value="Arabic">Arabic</option>
                        <option value="Malay">Malay</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-300 mb-1">Max Rate ($/hr)</label>
                      <input
                        type="number"
                        placeholder="e.g., 50"
                        value={maxRate}
                        onChange={(e) => setMaxRate(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-white text-slate-900 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-300 mb-1">Min Rating</label>
                      <select
                        value={minRating}
                        onChange={(e) => setMinRating(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-white text-slate-900 text-sm"
                      >
                        <option value="">Any Rating</option>
                        <option value="4.5">4.5+ Stars</option>
                        <option value="4.0">4.0+ Stars</option>
                        <option value="3.5">3.5+ Stars</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <button onClick={clearFilters} className="text-sm text-slate-300 hover:text-white flex items-center gap-1">
                      <X className="w-4 h-4" /> Clear
                    </button>
                    <button onClick={handleSearch} className="text-sm text-cyan-400 hover:text-cyan-300 font-semibold">
                      Apply Filters
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Trust Badges — left-aligned, matching search bar width */}
            <div className="max-w-2xl mx-auto flex gap-8 mt-8">
              <div className="flex flex-col items-start text-left">
                <Icon3D icon={Shield} colour="green" size="sm" className="mb-2" />
                <span className="font-semibold text-white text-sm">ID Verified</span>
                <p className="text-xs text-slate-400 mt-1">All tutors verified via Stripe</p>
              </div>
              <div className="flex flex-col items-start text-left">
                <Icon3D icon={Clock} colour="turquoise" size="sm" className="mb-2" />
                <span className="font-semibold text-white text-sm">5-Min Trial</span>
                <p className="text-xs text-slate-400 mt-1">Cancel free within 5 min</p>
              </div>
              <div className="flex flex-col items-start text-left">
                <Icon3D icon={Award} colour="blue" size="sm" className="mb-2" />
                <span className="font-semibold text-white text-sm">Secure Payment</span>
                <p className="text-xs text-slate-400 mt-1">Payment held prior to session</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA: Become a Tutor */}
        {!isRegisteredTutor && (
          <div className="max-w-7xl mx-auto px-4 mt-6">
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 flex items-center justify-between border border-cyan-200" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="flex items-center gap-3">
                <Icon3D icon={GraduationCap} colour="purple" size="md" />
                <div>
                  <p className="font-semibold text-slate-900">Want to earn money tutoring?</p>
                  <p className="text-sm text-slate-600">Share your knowledge and earn up to $75/hr helping other students.</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/tutoring/register')}
                className="px-5 py-2.5 rounded-lg text-white font-semibold text-sm whitespace-nowrap relative overflow-hidden"
                style={{ background: 'var(--gradient-primary)', boxShadow: 'var(--shadow-3d-btn)' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full" style={{ animation: 'shimmer 2.5s infinite' }} />
                <span className="relative z-10">Become a Tutor</span>
              </button>
            </div>
          </div>
        )}

        {/* Tutor Grid */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500">{error}</p>
              <button onClick={loadTutors} className="mt-4 text-cyan-600 hover:underline">Try Again</button>
            </div>
          ) : tutors.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700">No tutors found</h3>
              <p className="text-slate-500 mt-2">Try adjusting your search filters or check back later.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tutors.map((tutor) => (
                  <div
                    key={tutor.id}
                    className="bg-white rounded-xl border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer overflow-hidden"
                    style={{ boxShadow: 'var(--shadow-card)' }}
                    onClick={() => navigate(`/tutoring/tutor/${tutor.id}`)}
                  >
                    <div className="p-5">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                            style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212), rgb(8, 145, 178))' }}
                          >
                            {(tutor.tutor_name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{tutor.tutor_name || 'Anonymous'}</h3>
                            <p className="text-sm text-slate-500">
                              {tutor.institution || 'University'} • Year {tutor.current_year || '?'}
                            </p>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-cyan-600">
                          ${tutor.hourly_rate}/hr
                        </span>
                      </div>

                      {/* Bio */}
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                        {tutor.bio || 'No bio provided'}
                      </p>

                      {/* Subjects */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(tutor.subjects || []).slice(0, 4).map((subject, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 text-xs rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200"
                          >
                            {subject}
                          </span>
                        ))}
                        {(tutor.subjects || []).length > 4 && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-500">
                            +{tutor.subjects.length - 4} more
                          </span>
                        )}
                      </div>

                      {/* Languages */}
                      <div className="flex items-center gap-1.5 mb-3 text-sm text-slate-500">
                        <Globe className="w-3.5 h-3.5" />
                        {(tutor.languages || ['English']).join(', ')}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-1">
                          {renderStars(tutor.average_rating || 0)}
                          <span className="text-sm text-slate-500 ml-1">
                            ({tutor.rating_count || 0})
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span>{tutor.total_sessions || 0} sessions</span>
                          <ChevronRight className="w-4 h-4 text-cyan-500" />
                        </div>
                      </div>

                      {/* Trust Badge */}
                      {tutor.identity_verified && (
                        <div className="mt-3 flex items-center gap-1.5 text-xs text-green-600">
                          <Shield className="w-3.5 h-3.5" />
                          ID Verified
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg border border-slate-200 text-sm disabled:opacity-50 hover:bg-slate-50"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-slate-600">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg border border-slate-200 text-sm disabled:opacity-50 hover:bg-slate-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TutoringPage;
